import { Token } from "@/components/token";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { formatNumber } from "@/lib/utils";
import { useWallet } from "@txnlab/use-wallet";
import { MintForm } from "./components/mint";
import { NodeRunnerCard } from "./components/noderunner";
import { RedeemForm } from "./components/redeem";
import { UpgradeCard } from "./components/upgrade-card";
import { FeeUpgradeCard } from "./components/fee-upgrade-card";
import { FarmCard } from "./components/farm-card";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { BlocksProposedCard } from "@/components/blocks-proposed-card";
import { HoldersDialog } from "./components/holders-dialog";
import { StakedCard } from "./components/staked-card";
import { InfoBareCard } from "./info-bare-card";
import { FeesRolesBareCard } from "./fees-roles-bare-card";
import { AdminCard } from "./components/admin-card";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { useCState } from "@/hooks/use-cstate";
import { useNavigate, useParams } from "react-router-dom";
import { useListing } from "@/hooks/use-listings";
import { useFarmsAndAPRs } from "@/hooks/use-farms-and-aprs";
import {
  DSContractState
} from "@myth-finance/dualstake-ts-sdk";
import { SEO } from "@/components/seo";
import { DSContractListingExtd, DSFarmStateExtended } from "@/lib/types";
import { FullAprBreakdown } from "@/hooks/use-apr-breakdown";
import NotFound from "@/components/notfound";
import { Button } from "@/components/ui/button";
import { LayoutList } from "lucide-react";
import { SwapsCard } from "./components/swaps-card";
import { ActivityCard } from "./components/activity-card";
import { YourStakeCard } from "./components/your-stake-card";
import { StakedChart } from "./components/staked-chart";

function DualStakeInner({
  listing,
  cState,
  farm,
  aprBreakdown,
}: {
  listing: DSContractListingExtd;
  cState: DSContractState;
  farm?: DSFarmStateExtended;
  aprBreakdown: FullAprBreakdown;
}) {
  const { activeAddress } = useWallet();

  if (!listing) return null;
  const pairAsaId = Number(listing?.asaId ?? 0n);

  const showNodeRunner =
    activeAddress === cState?.noderunnerAddr ||
    activeAddress === cState?.feeAdminAddr;
  const nodeRunnerRole =
    activeAddress === cState?.noderunnerAddr ? "Node runner" : "Fee manager";

  const showAdmin =
    activeAddress === cState?.adminAddr ||
    activeAddress === cState?.feeAdminAddr;

  const textColors = getHighlightClasses(pairAsaId, "text");

  const { data: accountInfo } = useAlgoAcctInfo(activeAddress);
  const userLstBalance = listing?.lstId
    ? useBalance(listing?.lstId, accountInfo)
    : 0;

  return (
    <>
      {cState?.contractUpgrade || cState?.userProtestingStake !== 0n ? (
        <UpgradeCard cState={cState!} listing={listing!} />
      ) : null}
      {cState?.feeUpdate ? (
        <FeeUpgradeCard cState={cState} listing={listing!} />
      ) : null}
      <div className="flex flex-col gap-2 justify-center items-center container">
        <Token
          variant="lg"
          className="mb-2"
          leftAssetId={pairAsaId}
          rightAssetId={0}
        />
        {userLstBalance > 0 ? (
          <div className={`font-bold ${textColors}`}>
            You hold {formatNumber(userLstBalance, 6, 6)} {cState?.lstName}.
          </div>
        ) : null}
        {cState?.userProtestingStake !== 0n ? (
          <div className={`font-bold ${textColors}`}>
            You have locked {formatNumber(cState?.userProtestingStake!)}{" "}
            {cState?.lstName} in protest.
          </div>
        ) : null}
      </div>

      <div className="flex gap-4 flex-col items-start md:flex-row">
        <div className="flex flex-col hidden lg:flex mt-11 lg:w-[310px] xl:w-[350px]">
          <StakedCard className="w-full" cState={cState!} listing={listing} />

          <YourStakeCard cState={cState} className="mt-4" />

          {showNodeRunner && cState ? (
            <div className="flex flex-wrap justify-center mt-4 items-center">
              <NodeRunnerCard
                role={nodeRunnerRole}
                cState={cState}
                listing={listing!}
              />
            </div>
          ) : null}


          <StakedChart className="mt-4" cState={cState} />

          <InfoBareCard
            className="w-full mt-4 opacity-0 animate-fade-in-2"
            cState={cState!}
            listing={listing}
          />

          <FeesRolesBareCard
            className="w-full mt-2 opacity-0 animate-fade-in-3"
            cState={cState!}
          />

          <HoldersDialog
            cState={cState!}
            className="mt-1 opacity-0 animate-fade-in-4"
          />
        </div>

        <div className="flex flex-col gap-4 max-w-[350px]">
          <Tabs defaultValue={listing?.sunset ? "redeem" : "mint"}>
            <div className="max-w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="mint">Mint {listing!.lstName}</TabsTrigger>
                <TabsTrigger value="redeem">
                  Redeem {listing!.lstName}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent key={"mint"} value={"mint"}>
              <MintForm listing={listing!} cState={cState!} />
            </TabsContent>
            <TabsContent key={"redeem"} value={"redeem"}>
              <RedeemForm listing={listing!} cState={cState!} />
            </TabsContent>
          </Tabs>

          <ActivityCard className="hidden lg:block" cState={cState} />
        </div>

        <div className="flex-col hidden lg:flex gap-4 mt-11 lg:w-[310px] xl:w-[350px]">
          <FarmCard
            farm={farm}
            cState={cState}
            listing={listing}
            aprBreakdown={aprBreakdown}
          />

          {showAdmin && cState ? (
            <AdminCard cState={cState} listing={listing} />
          ) : null}

          <BlocksProposedCard listing={listing} />

          <SwapsCard cState={cState} />

        </div>
      </div>

      <div className="flex lg:hidden max-w-[350px] gap-4 flex-col flex-wrap justify-center items-center">
        <YourStakeCard cState={cState} />

        {showAdmin && cState ? (
          <div className="flex flex-wrap justify-center items-center">
            <AdminCard cState={cState} listing={listing!} />
          </div>
        ) : null}

        {showNodeRunner && cState ? (
            <div className="">
              <NodeRunnerCard
                role={nodeRunnerRole}
                cState={cState}
                listing={listing!}
              />
            </div>
          ) : null}

        <StakedCard className="" cState={cState!} listing={listing} />

        <FarmCard
          className=""
          farm={farm}
          cState={cState!}
          listing={listing!}
          aprBreakdown={aprBreakdown}
        />

        <ActivityCard className="max-w-[350px]" cState={cState} />

        <BlocksProposedCard listing={listing!} />

        <SwapsCard cState={cState} />

        <StakedChart cState={cState} />

        <InfoBareCard className="w-full mt-2" cState={cState!} listing={listing} />

        <FeesRolesBareCard className="w-full" cState={cState!} />

        <HoldersDialog className="self-start w-fit" cState={cState!} />
      </div>
    </>
  );
}

export function DualStakePage() {
  const { token } = useParams();
  const { activeAddress } = useWallet();

  const {
    isPending: loadingL,
    error: errorL,
    data: regularListing,
    refetch: refetchL,
  } = useListing(token!);
  const {
    isPending: loadingS,
    error: errorS,
    data: sunsetListing,
    refetch: refetchS,
  } = useListing(token!, undefined, true);
  
  const listing = regularListing ?? sunsetListing
  const appId = listing?.appId ?? 0n
  
  const {
    error: errorC,
    isPending: loadingC,
    data: cState,
    refetch: refetchC,
  } = useCState(appId, activeAddress);
  const {
    error: errorF,
    isPending: loadingF,
    data: farmsAndApr,
    refetch: refetchF,
  } = useFarmsAndAPRs(cState?.appId ? [cState] : []);

  const loading = loadingL || loadingC || loadingF || loadingS || !listing;
  const error = errorL ?? errorC ?? errorF ?? errorS;
  
  const retry = () => {
    if (errorL) refetchL();
    if (errorF) refetchF();
    if (errorC) refetchC();
    if (errorS) refetchS();
  };

  const farm = farmsAndApr?.get(appId)?.farm;
  const aprBreakdown = farmsAndApr?.get(appId)?.apr;

  const navigate = useNavigate();
  
  if (!loadingL && listing === null && sunsetListing === null) {
    return (
      <NotFound>
        <Button variant="secondary" onClick={() => navigate("/dualSTAKE")}>
          <LayoutList />
          Back to dualSTAKE listings
        </Button>
      </NotFound>
    );
  }

  return (
    <>
      {!listing?.lstName ? null : (
        <SEO
          type="product"
          title={`${listing.lstName} dualSTAKE`}
          description={`Stake ALGO+${listing.asaUnitName} > Earn in ${listing.asaUnitName}`}
          imageUrl={`${window.location.origin}/assets/dualstake-og-image/${listing.asaId}.png`}
        />
      )}
      <WithErrorAndLoading
        loading={loading}
        error={error?.message}
        retry={retry}
      >
        <DualStakeInner
          listing={listing!}
          cState={cState!}
          farm={farm}
          aprBreakdown={aprBreakdown!}
        />
      </WithErrorAndLoading>
    </>
  );
}
